import { WEATHER_MAP_LAYOUT_ORIENTATIONS } from 'features/Weather/features/WeatherMaps/config/weatherMapLayout.config';
import { createWeatherMapPollingQueryOptions } from 'features/Weather/features/WeatherMaps/utils/weatherMapQuery.utils';
import L from 'leaflet';
import hurricaneIconUrl from '../assets/red-hurricane.png';

// Route key shared with WidgetTimeUpdate / routeOffsetMinutes.
export const HURRICANES_ROUTE = 'getHurricanesLive';
export const HURRICANES_ACCESSIBLE_NAME = 'Hurricanes and tropical storms';
export const HURRICANES_WIDGET_ACCESSIBLE_NAME = `${HURRICANES_ACCESSIBLE_NAME} widget`;
export const HURRICANES_REFRESH_INTERVAL_MINUTES = 180;
export const HURRICANES_QUERY_OPTIONS = createWeatherMapPollingQueryOptions({
  refreshIntervalMinutes: HURRICANES_REFRESH_INTERVAL_MINUTES,
});

// Sentinel selector value that renders every active storm at once.
export const HURRICANES_ALL_LIVE_STORMS_KEY = 'all-live-storms';
export const HURRICANES_ALL_LIVE_STORMS_OPTION = Object.freeze({
  value: HURRICANES_ALL_LIVE_STORMS_KEY,
  label: 'All Live Storms',
});

export const HURRICANES_DATASETS = Object.freeze({
  live: 'live',
  historical: 'historical',
});

export const HURRICANES_DATASET_OPTIONS = Object.freeze([
  Object.freeze({ value: HURRICANES_DATASETS.live, label: 'Live' }),
  Object.freeze({ value: HURRICANES_DATASETS.historical, label: 'Historical' }),
]);

const createStormPositionIcon = ({ isActive = false, isForecast = false } = {}) => {
  const positionKind = isForecast ? 'forecast' : 'observed';
  const iconSize = isActive ? 40 : 24;
  const markerHtml = isActive
    ? `<span class="hurricane-active-position is-${positionKind}">
        <img alt="" aria-hidden="true" src="${hurricaneIconUrl}" />
        ${isForecast ? '<span aria-hidden="true" class="hurricane-forecast-badge">F</span>' : ''}
      </span>`
    : `<span aria-hidden="true" class="hurricane-track-position is-${positionKind}"></span>`;

  return L.divIcon({
    className: `hurricane-position-icon ${
      isActive ? 'hurricane-active-position-icon' : 'hurricane-track-position-icon'
    }`,
    html: markerHtml,
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize / 2],
    popupAnchor: [0, -(iconSize / 2 + 4)],
    tooltipAnchor: [0, -(iconSize / 2 + 4)],
  });
};

// Intermediate points remain compact while one larger hurricane symbol gives
// each storm a clear active position. Forecast variants use a hollow/dashed
// treatment so meaning is not communicated by color alone.
export const HURRICANES_STORM_POSITION_ICONS = Object.freeze({
  observed: createStormPositionIcon(),
  forecast: createStormPositionIcon({ isForecast: true }),
  activeObserved: createStormPositionIcon({ isActive: true }),
  activeForecast: createStormPositionIcon({ isActive: true, isForecast: true }),
});

// These colors describe map data, not application chrome. Every track uses a
// light casing so it remains readable on street, satellite, and dark tiles.
export const HURRICANES_STORM_MAP_PATH_OPTIONS = Object.freeze({
  forecastCone: Object.freeze({
    color: '#c83b4d',
    dashArray: '8 6',
    fillColor: '#ef5261',
    fillOpacity: 0.16,
    interactive: false,
    opacity: 0.95,
    weight: 2,
  }),
  historicalOutline: Object.freeze({
    color: '#64748b',
    dashArray: '6 7',
    fillColor: '#94a3b8',
    fillOpacity: 0.1,
    interactive: false,
    opacity: 0.9,
    weight: 2,
  }),
  observedTrackCasing: Object.freeze({
    color: '#ffffff',
    interactive: false,
    lineCap: 'round',
    lineJoin: 'round',
    opacity: 0.9,
    weight: 7,
  }),
  observedTrack: Object.freeze({
    color: '#174f78',
    interactive: false,
    lineCap: 'round',
    lineJoin: 'round',
    opacity: 1,
    weight: 3.5,
  }),
  forecastTrackCasing: Object.freeze({
    color: '#ffffff',
    dashArray: '8 7',
    interactive: false,
    lineCap: 'round',
    lineJoin: 'round',
    opacity: 0.95,
    weight: 7,
  }),
  forecastTrack: Object.freeze({
    color: '#c33143',
    dashArray: '8 7',
    interactive: false,
    lineCap: 'round',
    lineJoin: 'round',
    opacity: 1,
    weight: 3.5,
  }),
});

export const HURRICANES_REPLAY_SPEED_OPTIONS = Object.freeze([0.5, 1, 2, 5]);
export const HURRICANES_REPLAY_BASE_FRAME_INTERVAL_MS = 800;

// How storm position markers render while replaying:
// trail = markers accumulate as the replay fills in, current = one moving marker.
export const HURRICANES_REPLAY_MARKER_MODES = Object.freeze({
  trail: 'trail',
  current: 'current',
});
export const HURRICANES_DEFAULT_REPLAY_MARKER_MODE = HURRICANES_REPLAY_MARKER_MODES.trail;
export const HURRICANES_REPLAY_MARKER_OPTIONS = Object.freeze([
  Object.freeze({ value: HURRICANES_REPLAY_MARKER_MODES.trail, label: 'All Positions' }),
  Object.freeze({ value: HURRICANES_REPLAY_MARKER_MODES.current, label: 'Current Only' }),
]);

export const HURRICANES_PAGE_LAYOUT = Object.freeze({
  resizableOrientation: WEATHER_MAP_LAYOUT_ORIENTATIONS.horizontal,
  nonResizableOrientation: WEATHER_MAP_LAYOUT_ORIENTATIONS.vertical,
  mapDefaultSize: '66%',
  panelDefaultSize: '34%',
  mapMinSize: '320px',
  panelMinSize: '300px',
});
